// WWI signal post — a field-telephone pole: a tall timber pole carrying two
// crossarms studded with white insulator knobs, a small gunmetal junction box
// bolted at chest height, and a coil of field wire leaning at the base.
import * as THREE from "three";
import { defineModel, styleV3, type Part } from "../../lib";

const { box, cyl } = styleV3;

const TIMBER = "#8b6438";
const TIMBER_DK = "#6f4e2b";
const GUNMETAL = "#4a4d52";
const INSULATOR = "#e8e4c8";
const MUD = "#5d4a33";

const POLE_H = 1.15;

// A crossarm at height `y` with white insulator knobs at the tips and inboard.
function crossarm(y: number, len: number): Part[] {
  const parts: Part[] = [box(len, 0.045, 0.05, TIMBER_DK, [0, y, 0])];
  for (const x of [-len / 2 + 0.02, -len / 6, len / 6, len / 2 - 0.02]) {
    parts.push({
      geometry: new THREE.SphereGeometry(0.022, 6, 5),
      color: INSULATOR,
      position: [x, y + 0.035, 0],
    });
  }
  return parts;
}

// The wire coil leaning against the pole base: an upright spool (its axis
// nearly horizontal) tipped so its top rim rests toward the timber, a darker
// hub core poking through both faces.
function wireCoil(): Part[] {
  const lean: [number, number, number] = [1.25, 0.35, 0];
  const at: [number, number, number] = [0.06, 0.14, 0.11];
  return [
    cyl(0.11, 0.11, 0.05, 10, GUNMETAL, at, lean),
    cyl(0.08, 0.08, 0.07, 10, TIMBER_DK, at, lean),
  ];
}

export const signalPost = defineModel({
  id: "signal-post",
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  name: "Signal Post",
  tags: ["prop", "wwi", "military", "signals", "communication", "telephone"],
  summary: "A field-telephone pole with crossarms, insulators, a junction box and a wire coil.",
  author: "Fable 5",
  footprint: 0.3,
  parts(): Part[] {
    return [
      // Mud pad at the base.
      cyl(0.16, 0.19, 0.03, 8, MUD, [0, 0.015, 0]),
      // The pole, slightly tapered.
      cyl(0.03, 0.042, POLE_H, 7, TIMBER, [0, POLE_H / 2, 0]),
      // Two crossarms near the top, the upper one wider.
      ...crossarm(POLE_H - 0.08, 0.46),
      ...crossarm(POLE_H - 0.24, 0.36),
      // Junction box at chest height on the pole's front, with a small
      // insulator terminal on top.
      box(0.1, 0.14, 0.06, GUNMETAL, [0, 0.42, 0.06]),
      { geometry: new THREE.SphereGeometry(0.018, 6, 5), color: INSULATOR, position: [0, 0.5, 0.06] },
      ...wireCoil(),
    ];
  },
  animations: [],
});
